import { type ReactNode } from "react";
import type { ClientBranding } from "@/features/clients/types";
import { cn } from "@/lib/utils";
import { brandingStyle, panelRadiusClass, shadowClass } from "./branding";

interface ChatLayoutProps {
  header: ReactNode;
  messages: ReactNode;
  /**
   * The composer. Optional: a thread the visitor answers with buttons rather
   * than typing (onboarding) has nothing to put here, and an empty bar would
   * read as a broken input.
   */
  input?: ReactNode;
  branding?: ClientBranding | null;
  /**
   * Extra classes for the panel itself. Its default width is tuned for a chat
   * bubble; callers that put interactive blocks in the thread need more room.
   */
  className?: string;
  /**
   * `page` owns the viewport: edge to edge on a phone, a card on a backdrop above.
   * `preview` is the card alone, at the size of the embed's panel, for a page that
   * shows the chat among other things (the personalization settings) — the same
   * frame rather than a copy of it, so the preview cannot drift from the real one.
   */
  variant?: "page" | "preview";
}

export function ChatLayout({
  messages,
  input,
  header,
  branding,
  className,
  variant = "page",
}: ChatLayoutProps) {
  const panel = (
    <div
      style={variant === "preview" ? brandingStyle(branding) : undefined}
      className={cn(
        "relative flex w-full flex-col overflow-hidden bg-background",
        variant === "page"
          ? // Spelled out: Tailwind only generates classes it reads whole in
            // the source, so `md:` cannot be prefixed onto the shared constant.
            "h-dvh md:h-[min(720px,85vh)] md:max-w-md md:border md:rounded-[calc(var(--chat-radius,1rem)+4px)]"
          : cn("h-[560px] max-w-[400px] border", panelRadiusClass),
        shadowClass(branding?.shadow),
        className,
      )}
    >
      {header}
      {messages}
      {input}
    </div>
  );

  if (variant === "preview") return panel;

  return (
    // Plain, flat backdrop behind the floating card on larger screens; edge-to-edge
    // on mobile. No gradients — the visual language is flat. `branding` themes the
    // whole subtree via CSS variables (primary colour, corner radius) + card shadow.
    <div
      style={brandingStyle(branding)}
      className="relative flex min-h-dvh flex-col overscroll-none md:items-center md:justify-center md:bg-canvas"
    >
      {panel}
    </div>
  );
}
